import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { AgGridReact } from 'ag-grid-react';
import styles from './DocumentsList.module.scss';
import classNames from 'classnames';
import { ColDef, SelectionChangedEvent, ICellRendererParams } from 'ag-grid-community';
import { Loader } from '@ds';
import React from 'react';

const CELL_HEIGHT = 56;
const SELECTION_COLUMN_WIDTHS = [40, 400, 400, 400, 400, 100];

export enum SortOrder {
  Ascending = 0,
  Descending = 1,
}

export interface SortEvent {
  orderBy: string;
  sortOrder: SortOrder;
}

export interface CustomColDef extends ColDef {
  customSortable?: boolean;
}

interface DocumentsListProps {
  rowsData: any[];
  colDefs: CustomColDef[];
  containerClassName?: string;
  isLoading?: boolean;
  onSortChanged?: (event: SortEvent | null) => void;
  onSelectionChanged?: (selectedDocuments: any[]) => void;
  currentSortField?: string;
  currentSortOrder?: SortOrder;
}

interface CustomHeaderProps extends ICellRendererParams {
  displayName: string;
  customSortable: boolean;
  colId: string;
  currentSortField?: string;
  currentSortOrder?: SortOrder;
  onCustomSortChanged: (colId: string) => void;
}

const CustomHeader: React.FC<CustomHeaderProps> = ({
  displayName,
  customSortable,
  colId,
  currentSortField,
  currentSortOrder,
  onCustomSortChanged,
}) => {
  const isCurrentSortField = currentSortField === colId;

  return (
    <div
      className={classNames(styles['custom-header'], customSortable && styles['sortable'])}
      onClick={() => customSortable && onCustomSortChanged(colId)}
    >
      <span>{displayName}</span>
      {customSortable && (
        <span className={styles['sort-icon']}>
          {isCurrentSortField ? (
            currentSortOrder === SortOrder.Ascending ? (
              <span className={styles['sort-asc']}></span>
            ) : (
              <span className={styles['sort-desc']}></span>
            )
          ) : (
            <span className={styles['sort-default']}></span>
          )}
        </span>
      )}
    </div>
  );
};

const DocumentsList = ({
  rowsData,
  colDefs,
  containerClassName = '',
  isLoading,
  onSortChanged,
  onSelectionChanged,
  currentSortField,
  currentSortOrder,
}: DocumentsListProps) => {
  const handleCustomSortChanged = (colId: string) => {
    if (!onSortChanged) return;

    if (currentSortField === colId) {
      if (currentSortOrder === SortOrder.Descending) {
        onSortChanged({
          orderBy: colId,
          sortOrder: SortOrder.Ascending,
        });
      } else {
        onSortChanged(null);
      }
    } else {
      onSortChanged({
        orderBy: colId,
        sortOrder: SortOrder.Descending,
      });
    }
  };

  const columns = [
    {
      field: 'isSelected',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerName: '',
      sortable: false,
      width: SELECTION_COLUMN_WIDTHS[0],
    },
    ...colDefs.map((colDef, index) => {
      const { customSortable, ...restColDef } = colDef;
      return {
        ...restColDef,
        sortable: false,
        width: SELECTION_COLUMN_WIDTHS[index + 1],
        headerComponent: (params: any) => (
          <CustomHeader
            {...params}
            displayName={colDef.headerName || ''}
            customSortable={customSortable !== false}
            colId={colDef.field || ''}
            currentSortField={currentSortField}
            currentSortOrder={currentSortOrder}
            onCustomSortChanged={handleCustomSortChanged}
          />
        ),
      };
    }),
  ];

  const onSelectedRowsChanged = (event: SelectionChangedEvent<any, any>) => {
    onSelectionChanged && onSelectionChanged(event.api.getSelectedRows());
  };

  const loadingIcon = () => {
    return <Loader />;
  };

  return (
    <div className={classNames(styles['documents-list'], containerClassName)}>
      <div className="ag-theme-balham">
        <AgGridReact
          defaultColDef={{ resizable: false, unSortIcon: false }}
          rowData={rowsData}
          columnDefs={columns}
          rowSelection={'multiple'}
          suppressMovableColumns={true}
          headerHeight={CELL_HEIGHT}
          rowHeight={CELL_HEIGHT}
          domLayout="autoHeight"
          loading={isLoading}
          loadingOverlayComponent={loadingIcon}
          onSelectionChanged={onSelectedRowsChanged}
          enableBrowserTooltips={true}
        />
      </div>
    </div>
  );
};

export default DocumentsList;
